// ---------------------------------------------------------------------------
// Severity → tint color policy for the Access Legitimacy grid (design v6 §3.3).
//
// The wire `sensitivity` is `{ entity: string; severity: number } | null` — a
// per-tenant label plus a score, with NO slug to key the shared (slug-based)
// `SensitivityChip` on. The grid therefore colors by SEVERITY, reproducing the
// existing five-tint orange ramp so the grid is visually identical to today's
// design (the same fills `SensitivityChip`'s `LEVEL_STYLES` uses, lightest →
// darkest, each with its matching light/dark foreground).
//
// The wire `severity` is a normalized score in `[0, 1]` (0 = least sensitive,
// 1 = most). `severityToTint` maps that continuum onto the five discrete tints
// by scaling to `[0, 4]`, rounding to the nearest stop, and indexing the ramp:
//   0 → tint 1, 0.25 → 2, 0.5 → 3, 0.75 → 4, 1 → tint 5.
// Input is clamped into `[0, 1]` first, so any out-of-range score degrades
// gracefully to an end tint rather than crashing or rendering an undefined tint.
// Color is severity-derived, never label-derived, so the grid stays decoupled
// from the slug tier system.
// ---------------------------------------------------------------------------

/** A pill tint: paired Tailwind background + foreground class fragments. */
export interface SensitivityTint {
  background: string;
  foreground: string;
}

/**
 * The five-stop orange ramp keyed by discrete severity level (1 = lightest /
 * least sensitive, 5 = darkest / most). Hex fills + light/dark foregrounds are
 * the exact values `SensitivityChip` renders today (Figma `289:50781`), so the
 * grid-local chip is pixel-identical to the shared chip it replaces.
 */
export const SENSITIVITY_TINTS: Readonly<Record<1 | 2 | 3 | 4 | 5, SensitivityTint>> = {
  1: { background: 'bg-[#FFEACE]', foreground: 'text-dawn-black' }, // public
  2: { background: 'bg-[#FFDBA9]', foreground: 'text-dawn-black' }, // internal
  3: { background: 'bg-[#FFBB60]', foreground: 'text-dawn-black' }, // confidential
  4: { background: 'bg-[#FF9700]', foreground: 'text-dawn-white' }, // restricted
  5: { background: 'bg-[#F47600]', foreground: 'text-dawn-white' }, // highly confidential
};

/**
 * Maps a normalized severity score in `[0, 1]` to its ramp tint. Clamps the
 * input into range, scales onto the five stops (`severity * 4`), and rounds to
 * the nearest — so `0 → 1`, `0.25 → 2`, `0.5 → 3`, `0.75 → 4`, `1 → 5`. Any
 * out-of-range score resolves to an end tint rather than crashing or rendering
 * an undefined tint.
 */
export function severityToTint(severity: number): SensitivityTint {
  const clamped = Math.min(1, Math.max(0, severity));
  const level = (Math.round(clamped * 4) + 1) as 1 | 2 | 3 | 4 | 5;
  return SENSITIVITY_TINTS[level];
}
